import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

function Seo({ data, slug, meta = [], preLoadImages = [] }) {
  const { seoTitle: title, seoDescription: description, seoImage: image } = data
  const {
    contentfulGlobals: {
      siteUrl,
      siteTitle,
      siteDescription: { siteDescription },
      siteShareImage,
      twitterUsername,
    },
  } = useStaticQuery(
    graphql`
      query {
        contentfulGlobals {
          siteUrl
          siteTitle
          siteDescription {
            siteDescription
          }
          siteShareImage {
            file {
              url
            }
          }
          twitterUsername
        }
      }
    `
  )

  const url = `${siteUrl.replace(/\/$/, '')}/${
    slug === '' ? '' : `${slug.replace(/^\/+|\/+$/g, '')}/`
  }`
  const ogTitle = title ? `${title} | ${siteTitle}` : siteTitle
  const ogDescription = description?.seoDescription || siteDescription
  const ogImage = `https:${image ? image.file.url : siteShareImage.file.url}`
  const metaTags = [
    {
      name: `description`,
      content: ogDescription,
    },
    {
      name: `robots`,
      content: `follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large`,
    },
    {
      property: `og:title`,
      content: ogTitle,
    },
    {
      property: `og:description`,
      content: ogDescription,
    },
    {
      property: `og:image`,
      content: ogImage,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:url`,
      content: url,
    },
    {
      name: `twitter:card`,
      content: `summary_large_image`,
    },
    {
      name: `twitter:title`,
      content: ogTitle,
    },
    {
      name: `twitter:description`,
      content: ogDescription,
    },
  ]

  if (twitterUsername) {
    metaTags.push({
      name: `twitter:site`,
      content: `@${twitterUsername}`,
    })
  }

  const headLinks = [
    {
      rel: `canonical`,
      href: url
    }
  ]

  const preloadLinks = preLoadImages.map((imgSrc) => ({
    rel: 'preload',
    href: imgSrc.file.url,
    as: 'image',
    fetchpriority: "high",
    type: "image/webp"
  }))

  return (
    <Helmet
      htmlAttributes={{
        lang: 'en',
      }}
      title={ogTitle}
      meta={[...metaTags, ...meta]}
      link={[...headLinks, ...preloadLinks]}
    />
  )
}

Seo.propTypes = {
  data: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  meta: PropTypes.arrayOf(PropTypes.object),
}

export default Seo
